import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, Disclaimer } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import {
  LICENSE_REQUEST_STATUSES, MEDIA_TYPES, PROJECT_TYPES, USAGE_TYPES, MOOD_TAGS,
} from "@/lib/constants";
import {
  GENRES, SUB_GENRES, TERRITORY_OPTIONS, TERM_LENGTH_OPTIONS, BUDGET_RANGE_OPTIONS, OTHER,
} from "@/lib/lists";
import { SearchableSelect, OtherField } from "@/components/Controlled";
import { ArrowLeft, Save, Send, Music, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { licenseRequestDeleteEligibility } from "@/lib/deleteEligibility";
import { ReasonModal } from "@/components/ReasonModal";
import { CANCEL_REQUEST_REASONS } from "@/lib/lists";
import { getLifecycleStatus, lifecycleBadgeVariant } from "@/lib/withdrawGate";

// MP6 — License Request editor / detail.
// Sections: A. Requester · B. Project · C. Music Requested · D. Workflow.
// Match to Ready Track via a side panel listing only Client/Corporate-Ready tracks.
// Linked-track clearance card warns when the linked track is not submission-ready.

const BLANK = {
  // A. Requester
  requester_name: "", requester_company: "", requester_email: "", requester_phone: "",
  cis_id: "",
  // B. Project
  project_title: "", project_type: "", usage_type: "", media_type: "",
  territory: "", term: "", budget_range: "", deadline: "",
  // Master Correction — companion free-text fields for "Other" picks.
  territory_other: "", term_other: "", budget_range_other: "",
  // C. Music
  requested_track_title: "", requested_track_id: "",
  genre_requested: "", sub_genre_requested: "",
  mood_requested: [],         // controlled multi-select using platform-wide MOOD_TAGS
  mood_keywords: "",          // legacy free-text — preserved for back-compat
  genre_requested_other: "", sub_genre_requested_other: "",
  notes: "",
  // D. Workflow
  request_status: "New", assigned_reviewer: "", linked_submission_id: "",
};

const UNREADY_STATUSES = new Set([
  "Incomplete", "Needs Review", "Attorney Review Required", "Restricted", "Ineligible",
]);
const READY_STATUSES = new Set(["Client-Ready", "Corporate-Ready"]);

export default function LicenseRequestEditor({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(BLANK);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [readyTracks, setReadyTracks] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [matchOpen, setMatchOpen] = useState(searchParams.get("match") === "1");

  async function loadRecord() {
    if (mode === "edit" && id) {
      try {
        const r = await api.get(`/license-requests/${id}`);
        setData({ ...BLANK, ...r.data });
        setMeta(r.data);
      } catch (e) { toast.error(errMsg(e)); }
    }
  }

  // Load tracks (for catalog lookup) + ready tracks (for matching) + reviewers.
  useEffect(() => {
    api.get("/tracks").then(r => setTracks(r.data || [])).catch(() => {});
    api.get("/tracks/ready").then(r => setReadyTracks(r.data || [])).catch(() => {});
    // Reviewer pool — endpoint already exists; the backend filters to active
    // roles authorised for compliance/intake (super_admin, gfmp_reviewer,
    // catalog_manager, legal_review). Beta Tester and deactivated users are
    // excluded server-side. We additionally hide deactivated users client-side
    // as a belt-and-braces guard.
    api.get("/auth/users")
      .then(r => setReviewers((r.data || []).filter(u => u.is_active !== false && ["super_admin", "gfmp_reviewer", "catalog_manager", "legal_review"].includes(u.role))))
      .catch(() => setReviewers([]));
    loadRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  function setField(k, v) { setData(d => ({ ...d, [k]: v })); }

  const linkedTrack = useMemo(() => {
    if (!data.requested_track_id) return null;
    return tracks.find(t => t.id === data.requested_track_id) || null;
  }, [tracks, data.requested_track_id]);

  const isLinkedReady = linkedTrack && READY_STATUSES.has(linkedTrack.clearance_status);
  const isLinkedUnready = linkedTrack && UNREADY_STATUSES.has(linkedTrack.clearance_status);

  async function save() {
    setSaving(true);
    try {
      if (mode === "new") {
        const r = await api.post("/license-requests", data);
        toast.success("License request created");
        navigate(`/license-requests/${r.data.id}`, { replace: true });
      } else {
        const r = await api.put(`/license-requests/${id}`, data);
        toast.success("Saved");
        setMeta(r.data);
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function createSubmissionFromRequest() {
    if (linkedTrack && !isLinkedReady) {
      toast.error("Linked track is not Client/Corporate-Ready — cannot create submission yet.");
      return;
    }
    try {
      const payload = {
        request_id: meta?.request_id || "",
        requester_name: data.requester_name,
        company: data.requester_company,
        project_title: data.project_title,
        selected_track_id: data.requested_track_id || "",
        selected_track_title: data.requested_track_title || (linkedTrack?.track_title || ""),
        submission_status: "Draft",
        notes: data.notes,
      };
      const r = await api.post("/submissions", payload);
      toast.success("Draft submission created");
      // Link back on the request so the table + UI show the connection immediately.
      setField("linked_submission_id", r.data.id);
      if (mode === "edit" && id) {
        try {
          await api.put(`/license-requests/${id}`, { ...data, linked_submission_id: r.data.id });
        } catch (_e) { /* non-fatal */ }
      }
      navigate(`/submissions/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  }

  function chooseReadyTrack(t) {
    setField("requested_track_id", t.id);
    setField("requested_track_title", t.track_title);
    if (t.genre && !data.genre_requested) setField("genre_requested", t.genre);
    setMatchOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete("match");
    setSearchParams(next, { replace: true });
    toast.success(`Matched to ${t.track_title}`);
  }

  function openMatch() {
    setMatchOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set("match", "1");
    setSearchParams(next, { replace: true });
  }

  function nextActionHint() {
    if (data.request_status === "Closed" || data.request_status === "Declined") return "Closed — no further action.";
    if (!data.requested_track_id) return "Match to a Ready Track or continue triage.";
    if (linkedTrack && isLinkedUnready) return `Linked track is ${linkedTrack.clearance_status}. Resolve compliance before submission.`;
    if (linkedTrack && isLinkedReady && data.request_status !== "Approved for Submission") return "Linked track is submission-ready. Move status to Approved for Submission, then create a submission draft.";
    if (data.request_status === "Approved for Submission") return "Create Submission Draft to begin the package.";
    return "Continue review.";
  }

  // Phase A Run 2 follow-up — cancel modal wire-in (mirrors ConsultationRequestDetail).
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  async function submitCancel({ category, detail }) {
    setCancelBusy(true);
    try {
      await api.post(`/license-requests/${id}/cancel`, { category, detail });
      toast.success("License request cancelled. Audit record created.");
      setCancelOpen(false);
      await loadRecord();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCancelBusy(false);
    }
  }
  const lifecycleStatus = getLifecycleStatus(meta);
  const isCancelled = lifecycleStatus === "CANCELLED";

  return (
    <div data-testid="license-request-editor">
      <PageHeader
        title={mode === "new" ? "New License Request" : (meta?.project_title || "License Request")}
        subtitle={meta ? (
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs">{meta.request_id} · received {(meta.date_received || "").slice(0, 10)}</span>
            {isCancelled && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.14em] font-medium border ${lifecycleBadgeVariant(lifecycleStatus).className}`}
                data-testid="license-request-lifecycle-badge"
              >
                {lifecycleBadgeVariant(lifecycleStatus).label}
              </span>
            )}
          </span>
        ) : "Internal intake and review"}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/license-requests")}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            {mode === "edit" && (
              <Button
                variant="outline"
                onClick={createSubmissionFromRequest}
                data-testid="license-create-submission"
                disabled={linkedTrack ? !isLinkedReady : false}
                title={linkedTrack && !isLinkedReady ? "Linked track must be Client/Corporate-Ready" : ""}
              >
                <Send className="w-4 h-4 mr-2" />Create Submission Draft
              </Button>
            )}
            {mode === "edit" && !isCancelled && (
              <Button
                variant="outline"
                onClick={() => setCancelOpen(true)}
                data-testid="license-cancel-button"
                style={{borderColor:"hsl(0 70% 42%)", color:"hsl(0 70% 42%)"}}
              >
                Cancel Request
              </Button>
            )}
            <Button onClick={save} disabled={saving} data-testid="license-save-button" style={{ background: "hsl(347 72% 22%)" }}>
              <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save"}
            </Button>
          </>
        }
      />

      {/* Linked-track clearance awareness card */}
      {linkedTrack && (
        <Card className="mb-4" data-testid="linked-track-card">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <Music className="w-4 h-4 text-muted-foreground" />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{linkedTrack.track_title} <span className="text-xs text-muted-foreground">— {linkedTrack.artist}</span></div>
              <div className="text-xs text-muted-foreground font-mono">{linkedTrack.track_id}</div>
            </div>
            <StatusBadge status={linkedTrack.clearance_status} />
            {isLinkedReady ? (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded status-client" data-testid="linked-track-ready">
                <CheckCircle2 className="w-3 h-3" />Submission-ready
              </span>
            ) : isLinkedUnready ? (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded status-attorney" data-testid="linked-track-unready">
                <AlertTriangle className="w-3 h-3" />Not submission-ready
              </span>
            ) : null}
            <Button
              variant="ghost" size="sm" className="ml-auto"
              onClick={() => navigate(`/tracks/${linkedTrack.id}`)}
              data-testid="linked-track-open"
            >
              Open track <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* A. Requester Information */}
        <Section title="A. Requester information" testid="section-requester">
          <Field label="Requester name"><Input value={data.requester_name} onChange={e => setField("requester_name", e.target.value)} data-testid="license-requester-name" /></Field>
          <Field label="Requester company"><Input value={data.requester_company} onChange={e => setField("requester_company", e.target.value)} data-testid="license-requester-company" /></Field>
          <Field label="Requester email"><Input type="email" value={data.requester_email} onChange={e => setField("requester_email", e.target.value)} data-testid="license-requester-email" /></Field>
          <Field label="Requester phone"><Input value={data.requester_phone} onChange={e => setField("requester_phone", e.target.value)} data-testid="license-requester-phone" /></Field>
          <Field full label="CIS ID (future archive reference)"><Input value={data.cis_id} onChange={e => setField("cis_id", e.target.value)} className="font-mono" placeholder="CIS-xxxx (optional)" data-testid="license-cis-id" /></Field>
        </Section>

        {/* B. Project Information */}
        <Section title="B. Project information" testid="section-project">
          <Field full label="Project title"><Input value={data.project_title} onChange={e => setField("project_title", e.target.value)} data-testid="license-project-title" /></Field>
          <Field label="Project type">
            <Select value={data.project_type || "unset"} onValueChange={v => setField("project_type", v === "unset" ? "" : v)}>
              <SelectTrigger data-testid="license-project-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">—</SelectItem>
                {PROJECT_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Usage type">
            <Select value={data.usage_type || "unset"} onValueChange={v => setField("usage_type", v === "unset" ? "" : v)}>
              <SelectTrigger data-testid="license-usage-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">—</SelectItem>
                {USAGE_TYPES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Media type">
            <Select value={data.media_type || "unset"} onValueChange={v => setField("media_type", v === "unset" ? "" : v)}>
              <SelectTrigger data-testid="license-media-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">—</SelectItem>
                {MEDIA_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Territory">
            <SearchableSelect
              value={data.territory}
              onChange={(v) => { setField("territory", v); if (v !== OTHER) setField("territory_other", ""); }}
              options={TERRITORY_OPTIONS}
              placeholder="Select territory…"
              testid="license-territory"
            />
            <OtherField
              triggerValue={data.territory}
              value={data.territory_other}
              onChange={(v) => setField("territory_other", v)}
              label="Custom territory"
              testid="license-territory-other"
            />
          </Field>
          <Field label="Term">
            <SearchableSelect
              value={data.term}
              onChange={(v) => { setField("term", v); if (v !== OTHER) setField("term_other", ""); }}
              options={TERM_LENGTH_OPTIONS}
              placeholder="Select term length…"
              testid="license-term"
            />
            <OtherField
              triggerValue={data.term}
              value={data.term_other}
              onChange={(v) => setField("term_other", v)}
              label="Custom term"
              testid="license-term-other"
            />
          </Field>
          <Field label="Budget range">
            <SearchableSelect
              value={data.budget_range}
              onChange={(v) => { setField("budget_range", v); if (v !== OTHER) setField("budget_range_other", ""); }}
              options={BUDGET_RANGE_OPTIONS}
              placeholder="Select budget range…"
              testid="license-budget-range"
            />
            <OtherField
              triggerValue={data.budget_range}
              value={data.budget_range_other}
              onChange={(v) => setField("budget_range_other", v)}
              label="Custom budget"
              testid="license-budget-range-other"
            />
          </Field>
          <Field label="Deadline"><Input type="date" value={data.deadline} onChange={e => setField("deadline", e.target.value)} data-testid="license-deadline" /></Field>
        </Section>

        {/* C. Music Requested */}
        <Section
          title="C. Music requested"
          testid="section-music"
          headerAction={
            <Button variant="outline" size="sm" onClick={openMatch} data-testid="license-match-button">
              <Music className="w-4 h-4 mr-2" />Match to Ready Track
            </Button>
          }
        >
          <Field full label="Requested track title (free text)"><Input value={data.requested_track_title} onChange={e => setField("requested_track_title", e.target.value)} data-testid="license-track-title" /></Field>
          <Field full label="Matched catalog track">
            <Select value={data.requested_track_id || "unset"} onValueChange={v => setField("requested_track_id", v === "unset" ? "" : v)}>
              <SelectTrigger data-testid="license-track-select"><SelectValue placeholder="Select a catalog track (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">— None —</SelectItem>
                {tracks.map(t => <SelectItem key={t.id} value={t.id}>{t.track_title} — {t.artist} ({t.clearance_status})</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Genre requested">
            <SearchableSelect
              value={data.genre_requested}
              onChange={(v) => { setField("genre_requested", v); if (v !== OTHER) setField("genre_requested_other", ""); }}
              options={GENRES}
              placeholder="Select genre…"
              testid="license-genre-requested"
            />
            <OtherField
              triggerValue={data.genre_requested}
              value={data.genre_requested_other}
              onChange={(v) => setField("genre_requested_other", v)}
              label="Custom genre"
              testid="license-genre-requested-other"
            />
          </Field>
          <Field label="Sub-genre requested">
            <SearchableSelect
              value={data.sub_genre_requested}
              onChange={(v) => { setField("sub_genre_requested", v); if (v !== OTHER) setField("sub_genre_requested_other", ""); }}
              options={SUB_GENRES}
              placeholder="Select sub-genre…"
              testid="license-sub-genre-requested"
            />
            <OtherField
              triggerValue={data.sub_genre_requested}
              value={data.sub_genre_requested_other}
              onChange={(v) => setField("sub_genre_requested_other", v)}
              label="Custom sub-genre"
              testid="license-sub-genre-requested-other"
            />
          </Field>
          <Field full label="Mood tags requested">
            <MoodTagPicker
              value={data.mood_requested}
              onChange={(arr) => setField("mood_requested", arr)}
            />
          </Field>
          <Field full label="Free-text mood keywords (legacy)"><Input value={data.mood_keywords} onChange={e => setField("mood_keywords", e.target.value)} placeholder="optional free-text" data-testid="license-mood-keywords" /></Field>
          <Field full label="Notes"><Textarea rows={3} value={data.notes} onChange={e => setField("notes", e.target.value)} data-testid="license-notes" /></Field>
        </Section>

        {/* D. Workflow */}
        <Section title="D. Workflow" testid="section-workflow">
          <Field label="Request status">
            <Select value={data.request_status} onValueChange={v => setField("request_status", v)}>
              <SelectTrigger data-testid="license-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LICENSE_REQUEST_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Assigned reviewer">
            <SearchableSelect
              value={data.assigned_reviewer}
              onChange={(v) => setField("assigned_reviewer", v)}
              options={reviewers.map((r) => ({ value: r.email, label: `${r.name || r.email} — ${roleLabelShort(r.role)}` }))}
              placeholder={reviewers.length === 0 ? "No authorised reviewers available" : "Select a reviewer…"}
              testid="license-assigned-reviewer"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Only active Super Admin, GFMP Reviewer, Catalog Manager, and Legal Review users are eligible. Beta Testers and deactivated users are excluded.
            </div>
          </Field>
          <Field full label="Linked submission">
            {data.linked_submission_id ? (
              <div className="flex items-center gap-2" data-testid="license-linked-submission-link">
                <code className="text-xs font-mono text-muted-foreground flex-1 truncate">{data.linked_submission_id}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/submissions/${data.linked_submission_id}`)}
                  data-testid="license-open-submission-button"
                >
                  Open submission <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setField("linked_submission_id", "")}
                  data-testid="license-unlink-submission-button"
                >
                  Unlink
                </Button>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground p-2 rounded bg-secondary/40" data-testid="license-no-submission-yet">
                {linkedTrack
                  ? (isLinkedReady
                    ? "Linked track is submission-ready. Use \u201cCreate Submission Draft\u201d above to generate the link."
                    : `Linked track is currently ${linkedTrack.clearance_status}. Cannot create a submission draft until it is Client-Ready or Corporate-Ready.`)
                  : "No matched track yet. Match a Ready Track first to enable the submission draft."}
              </div>
            )}
          </Field>
          <div className="md:col-span-2 mt-1 p-3 rounded-md bg-secondary/40" data-testid="license-next-action">
            <div className="text-xs text-muted-foreground">Suggested next action</div>
            <div className="text-sm">{nextActionHint()}</div>
          </div>
        </Section>
      </div>

      <Disclaimer>
        License request handling is for internal workflow and compliance support only · Not legal advice · Rights status must be verified before external submission.
      </Disclaimer>

      {/* MP11 Phase 2 — Danger zone: whole-record delete for a License Request.
          Backend enforces RBAC; client eligibility is a UI courtesy. */}
      {id && data?.id && (() => {
        const elig = licenseRequestDeleteEligibility(data, user);
        return (
          <DeleteRecordButton
            recordType="license_request"
            recordLabel="License Request"
            endpoint={`/license-requests/${data.id}`}
            confirmToken={data.request_id || (data.id || "").slice(0, 8).toUpperCase()}
            confirmTokenLabel="request id"
            disabled={!elig.allowed}
            disabledReason={elig.reason}
            onDeleted={() => { navigate("/license-requests"); }}
            testidPrefix="license-request-delete"
            isSuperAdmin={user?.role === "super_admin"}
          />
        );
      })()}

      <ReasonModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel License Request"
        description="Soft-cancels this license request. Prior notes, files, and audit history are preserved. Reason is required."
        categoryOptions={CANCEL_REQUEST_REASONS}
        confirmLabel="Cancel Request"
        severity="high"
        busy={cancelBusy}
        testid="cancel-license-modal"
        onConfirm={submitCancel}
      />

      <Sheet open={matchOpen} onOpenChange={(v) => { setMatchOpen(v); if (!v) { const n = new URLSearchParams(searchParams); n.delete("match"); setSearchParams(n, { replace: true }); } }}>
        <SheetContent side="right" className="!max-w-[520px] w-full overflow-y-auto" data-testid="license-match-sheet">
          <SheetHeader>
            <SheetTitle>Match to a Ready Track</SheetTitle>
          </SheetHeader>
          <p className="text-xs text-muted-foreground mt-2">
            Only Client-Ready and Corporate-Ready tracks appear. Manual selection for Internal Alpha.
          </p>
          <div className="mt-4 space-y-2">
            {readyTracks.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No tracks are currently Client/Corporate-Ready.</div>
            ) : readyTracks.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => chooseReadyTrack(t)}
                data-testid={`license-match-row-${t.id}`}
                className="w-full text-left p-3 rounded border hover:border-[hsl(347_72%_22%)]/60 transition flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.track_title} <span className="text-xs text-muted-foreground">— {t.artist}</span></div>
                  <div className="text-[11px] text-muted-foreground font-mono">{t.track_id} · {t.genre || "—"} · {(t.mood_tags || []).slice(0, 3).join(", ") || "—"}</div>
                </div>
                <StatusBadge status={t.clearance_status} />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Section({ title, children, testid, headerAction }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="font-display text-base">{title}</div>
          {headerAction && <div className="ml-auto">{headerAction}</div>}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {children}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function roleLabelShort(r) {
  return ({
    super_admin: "Super Admin",
    gfmp_reviewer: "GFMP Reviewer",
    catalog_manager: "Catalog Manager",
    legal_review: "Legal Review",
  })[r] || r;
}

// Lightweight mood multi-select reusing the platform-wide MOOD_TAGS list.
// Renders chips for selected values + a searchable dropdown to add more.
function MoodTagPicker({ value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const available = MOOD_TAGS.filter((t) => !selected.includes(t));
  return (
    <div data-testid="license-mood-picker">
      <div className="flex flex-wrap items-center gap-1.5 border border-input rounded-md bg-transparent px-2 py-1.5 min-h-[2.25rem]">
        {selected.length === 0 && <span className="text-xs text-muted-foreground">No mood tags selected.</span>}
        {selected.map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs">
            {t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              onClick={() => onChange(selected.filter((x) => x !== t))}
              className="opacity-60 hover:opacity-100"
              data-testid={`license-mood-remove-${i}`}
            >×</button>
          </span>
        ))}
      </div>
      <div className="mt-2 max-w-[280px]">
        <SearchableSelect
          value=""
          onChange={(v) => {
            if (!v) return;
            if (!selected.includes(v)) onChange([...selected, v]);
          }}
          options={available}
          placeholder="Add a mood tag…"
          allowClear={false}
          testid="license-mood-add"
        />
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">
        Same standardized mood list used in Catalog Tracks and Ready Tracks.
      </div>
    </div>
  );
}
